import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, WhatsappLogo, Copy, Gift, DownloadSimple } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GiftCardPage() {
  const { ref } = useParams();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/gifts/card/${ref}`)
      .then(r => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then(data => data && setGift(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [ref]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — paste into WhatsApp");
  };

  const shareWhatsApp = () => {
    const msg = gift
      ? `🎁 ${gift.from_name} sent you a RideXelite gift: ${gift.service_name}. Redeem here: ${shareUrl}`
      : shareUrl;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) {
    return <div className="max-w-lg mx-auto py-32 text-center text-zinc-400">Loading gift card…</div>;
  }
  if (notFound || !gift) {
    return (
      <div className="max-w-lg mx-auto py-32 text-center px-6">
        <div className="eyebrow gold-text mb-3">Gift not found</div>
        <h1 className="font-serif-display text-3xl text-white mb-3">This link no longer exists.</h1>
        <p className="text-zinc-400 text-sm">Ask the sender to resend the gift card link.</p>
      </div>
    );
  }

  const dateStr = gift.scheduled_at
    ? new Date(gift.scheduled_at).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    : null;
  const regionFlag = gift.region === "liberia" ? "🇱🇷" : "🇺🇸";

  return (
    <div className="min-h-[calc(100vh-4rem)] px-4 py-10 sm:py-16 flex flex-col items-center" data-testid="gift-card-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        data-testid="gift-card"
        className="relative w-full max-w-[560px] aspect-[3/4] rounded-[2rem] overflow-hidden shadow-[0_40px_120px_-30px_rgba(212,175,55,0.5)] border border-[#D4AF37]/40"
      >
        {/* backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1610] via-[#0B0B0D] to-[#1a1815]" />
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(80% 60% at 50% 0%, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)",
          }}
        />
        <div className="grain-overlay opacity-30" />

        <div className="relative h-full flex flex-col p-8 sm:p-10">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/40 bg-black/40">
              <Sparkle weight="fill" size={10} className="text-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">RideXelite · Gift</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{regionFlag}</div>
          </div>

          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">TO</div>
            <div className="font-serif-display text-4xl sm:text-5xl text-white leading-tight mt-2">
              {gift.recipient_name}
            </div>
          </div>

          {gift.gift_message && (
            <blockquote className="mt-6 pl-4 border-l-2 border-[#D4AF37]/60 text-zinc-200 text-base leading-relaxed italic">
              "{gift.gift_message}"
            </blockquote>
          )}

          <div className="mt-8 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md p-5">
            <div className="flex items-center gap-2 mb-2">
              <Gift weight="fill" size={12} className="text-[#D4AF37]" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">The gift</div>
            </div>
            <div className="font-serif-display text-xl text-white leading-tight">{gift.service_name}</div>
            {dateStr && (
              <div className="mt-2 text-xs text-zinc-400">Scheduled for {dateStr}</div>
            )}
          </div>

          <div className="mt-auto pt-8 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">From</div>
              <div className="font-serif-display italic text-xl gold-text mt-1">{gift.from_name}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Reservation</div>
              <div className="text-[#D4AF37] tracking-widest text-xs mt-1">{gift.reservation_ref}</div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-white/10 text-[10px] text-zinc-500 text-center tracking-wider">
            {gift.dispatcher_name ? `${gift.dispatcher_name}` : "Our dispatcher"} will reach out within 24 hours to arrange delivery.
          </div>
        </div>
      </motion.div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="gift-card-actions">
        <button
          onClick={shareWhatsApp}
          data-testid="share-whatsapp-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-black text-sm font-semibold hover:brightness-110 transition"
        >
          <WhatsappLogo weight="fill" size={16} /> Share on WhatsApp
        </button>
        <button
          onClick={copyLink}
          data-testid="copy-link-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-sm hover:bg-[#D4AF37] hover:text-black transition"
        >
          <Copy weight="fill" size={16} /> Copy link
        </button>
        <button
          onClick={() => window.print()}
          data-testid="print-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:border-white/40 transition"
        >
          <DownloadSimple weight="bold" size={16} /> Save as PDF
        </button>
      </div>
      <p className="mt-4 text-xs text-zinc-500 text-center max-w-md">
        Tip: on mobile, take a screenshot of the card above and send it in WhatsApp for a personal touch.
      </p>
    </div>
  );
}
